import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  Alert,
  List,
  ListItem,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../theme/panelStyles";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { sellerlogicApi } from "../../../services/api/plenty/sellerlogic";

const SellerlogicCredentials = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme);

  const [SellerlogicCredentials, setSellerlogicCredentials] = useState(() => {
    try {
      const stored = localStorage.getItem("sellerlogicCredentials");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [sellerlogicCredentialDialog, setSellerlogicCredentialDialog] =
    useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [formData, setFormData] = useState({
    display_name: "",
    client_id: "",
    client_secret: "",
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    fetchSellerlogicCredentials();
  }, []);

  useEffect(() => {
    if (snackbarOpen) {
      const timer = setTimeout(() => setSnackbarOpen(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbarOpen]);

  const fetchSellerlogicCredentials = async () => {
    try {
      const response = await sellerlogicApi.getCredentials();
      const credentials = response.data;
      // Test each credential
      const testedCredentials = await Promise.all(
        credentials.map(async (cred) => {
          try {
            const testResponse = await sellerlogicApi.testCredential(cred.id);
            return {
              ...cred,
              status: testResponse.data.valid ? "connected" : "disconnected",
            };
          } catch (error) {
            return { ...cred, status: "error" };
          }
        }),
      );
      setSellerlogicCredentials(testedCredentials);
      localStorage.setItem(
        "sellerlogicCredentials",
        JSON.stringify(testedCredentials),
      );
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error fetching Sellerlogic credentials:", error);
    }
  };

  const handleCreateCredential = async () => {
    try {
      setLoading(true);
      await sellerlogicApi.createCredential(formData);
      setSellerlogicCredentialDialog(false);
      setFormData({ display_name: "", client_id: "", client_secret: "" });
      fetchSellerlogicCredentials();
    } catch (error) {
      console.error("Error creating credential:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCredential = async () => {
    try {
      setLoading(true);
      await sellerlogicApi.updateCredential(editingCredential.id, formData);
      setSellerlogicCredentialDialog(false);
      setEditingCredential(null);
      setFormData({ display_name: "", client_id: "", client_secret: "" });
      fetchSellerlogicCredentials();
    } catch (error) {
      console.error("Error updating credential:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    if (window.confirm("Are you sure you want to delete this credential?")) {
      try {
        await sellerlogicApi.deleteCredential(credentialId);
        fetchSellerlogicCredentials();
      } catch (error) {
        console.error("Error deleting credential:", error);
      }
    }
  };

  const toggleSellerlogicCredential = async (credentialId) => {
    try {
      await sellerlogicApi.toggleActiveCredential(credentialId);
      fetchSellerlogicCredentials();
    } catch (error) {
      console.error("Error toggling credential:", error);
    }
  };

  const testSellerlogicCredential = async (credentialId) => {
    try {
      setLoading(true);
      const response = await sellerlogicApi.testCredential(credentialId);
      // Refresh credentials to update status
      fetchSellerlogicCredentials();
      alert(
        response.data.valid
          ? "Credential test successful!"
          : "Credential test failed!",
      );
    } catch (error) {
      console.error("Error testing credential:", error);
      alert("Credential test failed!");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSellerlogicCredential = (credential) => {
    setEditingCredential(credential);
    setFormData({
      display_name: credential.display_name || "",
      client_id: credential.client_id || "",
      client_secret: credential.client_secret || "",
    });
    setSellerlogicCredentialDialog(true);
  };

  const testFormCredentials = async () => {
    try {
      setLoading(true);
      const response = await sellerlogicApi.testCredentialKey({
        client_id: formData.client_id,
        client_secret: formData.client_secret,
      });
      alert(
        response.data.valid
          ? "Credential test successful!"
          : "Credential test failed!",
      );
    } catch (error) {
      console.error("Error testing credentials:", error);
      alert("Credential test failed!");
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setSellerlogicCredentialDialog(false);
    setEditingCredential(null);
    setFormData({ display_name: "", client_id: "", client_secret: "" });
  };

  return (
    <>
      {/* SellerLogic Credentials Tab */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={cp.outerCard}>
            <CardContent sx={{ p: 4 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={4}
                sx={{
                  ...cp.header,
                  p: 4,
                  borderRadius: 2,
                  color: "white",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    marginRight: "auto",
                    paddingRight: 2,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                    🔐 Sellerlogic API Credentials
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Manage your Sellerlogic API access credentials
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, marginLeft: "auto" }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchSellerlogicCredentials}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Reload
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setSellerlogicCredentialDialog(true)}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Add Credentials
                  </Button>
                </Box>
              </Box>

              <Alert
                severity="info"
                sx={{
                  mb: 4,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "info.main",
                  background:
                    "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                }}
                icon={<CheckCircleIcon sx={{ color: "info.main" }} />}
              >
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  📋 How to Get Your Sellerlogic Credentials:
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 3 }}>
                  <li>Login to Sellerlogic platform</li>
                  <li>Complete 2FA verification</li>
                  <li>
                    Obtain your Client ID and Client Secret from application
                    details
                  </li>
                  <li>Enter the credentials in the form below</li>
                </Box>
                <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic" }}>
                  For more information, visit{" "}
                  <a
                    href="https://app.sellerlogic.com/userapisettings"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#1976d2",
                      textDecoration: "underline",
                      fontWeight: "bold",
                    }}
                  >
                    SELLERLOGIC API Access
                  </a>
                </Typography>
              </Alert>

              {SellerlogicCredentials.length === 0 ? (
                <Box sx={cp.emptyState}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "text.secondary" }}
                  >
                    No Credentials Found
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, color: "text.secondary" }}
                  >
                    Add your first Sellerlogic credential to get started
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setSellerlogicCredentialDialog(true)}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      ...cp.ctaButton,
                    }}
                  >
                    Add Your First Credential
                  </Button>
                </Box>
              ) : (
                <List
                  sx={{
                    width: "100%",
                    border: "2px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    boxShadow: 2,
                  }}
                >
                  {SellerlogicCredentials.map((credential, index) => (
                    <ListItem
                      key={credential.id}
                      divider={index < SellerlogicCredentials.length - 1}
                      sx={{
                        px: 4,
                        py: 3,
                        borderBottom:
                          index < SellerlogicCredentials.length - 1
                            ? "2px solid"
                            : "none",
                        borderColor: "divider",
                        "&:hover": {
                          backgroundColor: "action.hover",
                          transform: "translateX(4px)",
                        },
                        transition: "all 0.2s ease",
                        borderRadius:
                          index === 0
                            ? "8px 8px 0 0"
                            : index === SellerlogicCredentials.length - 1
                              ? "0 0 8px 8px"
                              : 0,
                      }}
                    >
                      <Grid container alignItems="center" spacing={3}>
                        <Grid item xs={12} md={8}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography variant="h6" sx={{ mr: 2 }}>
                              {credential.display_name}
                            </Typography>
                            <Chip
                              label={credential.status}
                              size="small"
                              color={
                                credential.status === "connected"
                                  ? "success"
                                  : "error"
                              }
                              sx={{
                                fontWeight: "bold",
                                borderRadius: 2,
                                boxShadow: 1,
                              }}
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: "break-all",
                              pr: 2,
                              color: "text.secondary",
                              fontFamily: "monospace",
                              backgroundColor: "grey.100",
                              p: 1,
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "grey.300",
                            }}
                          >
                            Client ID: {credential.client_id}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{
                            display: "flex",
                            justifyContent: { xs: "center", md: "flex-end" },
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {/* Active Toggle Switch */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              mr: 2,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={credential.active_plenty}
                                  onChange={() =>
                                    toggleSellerlogicCredential(credential.id)
                                  }
                                  size="small"
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                      color: "success.main",
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                      {
                                        backgroundColor: "success.main",
                                      },
                                  }}
                                />
                              }
                              label=""
                              sx={{ m: 0 }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: credential.active_plenty
                                  ? "success.main"
                                  : "text.secondary",
                                fontWeight: "bold",
                                mt: 0.5,
                              }}
                            >
                              {credential.active_plenty ? "Active" : "Inactive"}
                            </Typography>
                          </Box>

                          <IconButton
                            onClick={() =>
                              testSellerlogicCredential(credential.id)
                            }
                            disabled={
                              loading ||
                              !credential.display_name ||
                              !credential.client_id
                            }
                            color="info"
                            title="Test Credentials"
                            sx={{
                              backgroundColor: "info.main",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "info.dark",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            {loading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <CheckCircleIcon />
                            )}
                          </IconButton>
                          <IconButton
                            title="Edit Credential"
                            onClick={() =>
                              handleEditSellerlogicCredential(credential)
                            }
                            color="warning"
                            sx={{
                              backgroundColor: "warning.main",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "warning.dark",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() =>
                              handleDeleteCredential(credential.id)
                            }
                            title="Delete Credential"
                            sx={{
                              backgroundColor: "error.main",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "error.dark",
                                transform: "scale(1.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Credential Dialog */}
      <Dialog
        open={sellerlogicCredentialDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            ...cp.dialogPaper,
          },
        }}
      >
        <DialogTitle
          sx={{
            ...cp.dialogHeader,
            color: "white",
            textAlign: "center",
            py: 3,
            fontSize: "1.5rem",
            fontWeight: "bold",
            borderRadius: "12px 12px 0 0",
          }}
        >
          {editingCredential ? "✏️ Edit Credential" : "➕ Add New Credential"}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              Enter your Sellerlogic API credentials below. All fields are
              required.
            </Typography>
          </Box>

          <TextField
            autoFocus
            margin="dense"
            label="Display Name"
            fullWidth
            variant="outlined"
            value={formData.display_name}
            onChange={(e) =>
              setFormData({ ...formData, display_name: e.target.value })
            }
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.paper",
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                  borderWidth: 2,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: "primary.main" }}>🏷️</Box>
              ),
            }}
          />

          <TextField
            margin="dense"
            label="Client ID"
            fullWidth
            variant="outlined"
            value={formData.client_id}
            onChange={(e) =>
              setFormData({ ...formData, client_id: e.target.value })
            }
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.paper",
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                  borderWidth: 2,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: "primary.main" }}>🔑</Box>
              ),
            }}
          />

          <TextField
            margin="dense"
            label="Client Secret"
            fullWidth
            variant="outlined"
            type="password"
            value={formData.client_secret}
            onChange={(e) =>
              setFormData({ ...formData, client_secret: e.target.value })
            }
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.paper",
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                  borderWidth: 2,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: "primary.main" }}>🔒</Box>
              ),
            }}
          />

          <Alert
            severity="warning"
            sx={{
              mt: 3,
              borderRadius: 2,
              ...cp.warningBanner,
            }}
            icon={<CheckCircleIcon sx={{ color: "warning.main" }} />}
          >
            <Typography variant="body2">
              <strong>Security Note:</strong> Your credentials are encrypted and
              stored securely. Make sure to keep your Client Secret
              confidential.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 0,
            justifyContent: "space-between",
            backgroundColor: "grey.50",
            borderRadius: "0 0 12px 12px",
          }}
        >
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={testFormCredentials}
            variant="outlined"
            disabled={
              loading ||
              !formData.client_id.trim() ||
              !formData.client_secret.trim()
            }
            sx={{
              borderRadius: 2,
              px: 3,
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            {loading ? <CircularProgress size={20} /> : "Test Credentials"}
          </Button>
          <Button
            onClick={
              editingCredential
                ? handleUpdateCredential
                : handleCreateCredential
            }
            variant="contained"
            disabled={
              loading ||
              !formData.display_name.trim() ||
              !formData.client_id.trim() ||
              !formData.client_secret.trim()
            }
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              ...cp.ctaButton,
              "&:hover": {
                background: "linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: "grey.400",
                color: "white",
              },
              transition: "all 0.3s ease",
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Processing...
              </Box>
            ) : editingCredential ? (
              "Update Credential"
            ) : (
              "Create Credential"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Credentials reloaded successfully"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

export default SellerlogicCredentials;
